import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getJobs, closeJob, deleteJob } from '../../services/firestoreService';
import { useAuth } from '../../contexts/AuthContext';
import JobReferralModal from '../../components/jobs/JobReferralModal';
import ApplicationModal from '../../components/applications/ApplicationModal';
import ArchiveManager from '../../components/admin/ArchiveManager';

const JobBoard = () => {
  const { currentUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('JobBoard component mounted, loading jobs...');
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading jobs from Firebase...');
      const jobsData = await getJobs(20); // Load up to 20 jobs
      console.log('Jobs loaded successfully:', jobsData);
      console.log('Number of jobs:', jobsData.length);

      setJobs(jobsData);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setError('Failed to load jobs. Please try again.');
      setJobs([])
    } finally {
      setLoading(false);
    }
  };



  // State for filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  // State for referral modal
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [selectedJobForReferral, setSelectedJobForReferral] = useState(null);

  // State for application modal
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedJobForApplication, setSelectedJobForApplication] = useState(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(10);

  // Simple function to check if job should show closed message
  const shouldShowClosedMessage = (job) => {
    return job.status === 'closed';
  };

  // Function to handle marking job as filled
  const handleMarkAsFilled = async (jobId) => {
    if (!currentUser) return;

    try {
      await closeJob(jobId, currentUser.uid);
      // Refresh the jobs list
      loadJobs();
      alert('Job marked as filled successfully!');
    } catch (error) {
      console.error('Error marking job as filled:', error);
      alert('Failed to mark job as filled. Please try again.');
    }
  };

  // Function to check if current user owns the job
  const isJobOwner = (job) => {
    return currentUser && (job.postedBy === currentUser.uid || job.contactEmail === currentUser.email);
  };

  // Function to check if current user is admin
  const isAdmin = () => {
    return currentUser && currentUser.email === 'admin@the-apn.com';
  };

  // Function to handle deleting job (admin only)
  const handleDeleteJob = async (jobId, jobTitle) => {
    if (!isAdmin()) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete the job "${jobTitle}"? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      await deleteJob(jobId);
      // Refresh the jobs list
      loadJobs();
      alert('Job deleted successfully!');
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Failed to delete job. Please try again.');
    }
  };

  // Use real jobs data only
  const displayJobs = jobs;

  // Debug logging
  console.log('Jobs state:', jobs);
  console.log('Display jobs:', displayJobs);

  // Handle referral modal
  const handleReferClick = (job) => {
    setSelectedJobForReferral(job);
    setShowReferralModal(true);
  };

  const handleReferralSuccess = (referralData) => {
    console.log('Referral submitted successfully:', referralData);
    // You could show a toast notification here
    alert('Referral submitted successfully! Thank you for helping connect talent with opportunities.');
  };

  // Get unique values for filter dropdowns
  const locations = [...new Set(displayJobs.map(job => job.location))];
  const types = [...new Set(displayJobs.map(job => job.type))];

  // Predefined company types with dynamic ones from jobs
  const predefinedCompanyTypes = [
    'Investment Bank',
    'Private Equity',
    'Hedge Fund',
    'Asset Management',
    'Consulting',
    'Corporate',
    'Venture Capital',
    'Insurance',
    'Real Estate',
    'Fintech',
    'Accounting Firm',
    'Law Firm'
  ];
  const jobCompanyTypes = [...new Set(displayJobs.map(job => job.companyType))].filter(Boolean);
  const companyTypes = [...new Set([...predefinedCompanyTypes, ...jobCompanyTypes])];

  // Filter jobs based on search and filters
  const filteredJobs = displayJobs.filter(job => {
    const matchesSearch =
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = locationFilter === '' || job.location === locationFilter;
    const matchesType = typeFilter === '' || job.type === typeFilter;
    const matchesCompanyType = companyTypeFilter === '' || job.companyType === companyTypeFilter;
    const matchesFeatured = !showFeaturedOnly || job.featured;

    return matchesSearch && matchesLocation && matchesType && matchesCompanyType && matchesFeatured;
  });

  // Sort jobs by posted date (newest first)
  const sortedJobs = [...filteredJobs].sort((a, b) =>
    new Date(b.postedDate) - new Date(a.postedDate)
  );

  // Pagination logic
  const totalPages = Math.ceil(sortedJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = sortedJobs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue mx-auto mb-4"></div>
          <p>Loading jobs from Firebase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Error Loading Jobs</h3>
          <p className="text-red-700">{error}</p>
          <button
            onClick={loadJobs}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Job Board</h1>
          </div>
        </div>
      </div>

      {/* Job Board Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="w-full md:w-1/2">
                <input
                  type="text"
                  placeholder="Search jobs by title, company, or keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md ${
                    viewMode === 'list'
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${
                    viewMode === 'grid'
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="location-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Location
                </label>
                <select
                  id="location-filter"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Locations</option>
                  {locations.map((location, index) => (
                    <option key={index} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Job Type
                </label>
                <select
                  id="type-filter"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Types</option>
                  {types.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="company-type-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Company Type
                </label>
                <select
                  id="company-type-filter"
                  value={companyTypeFilter}
                  onChange={(e) => setCompanyTypeFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Company Types</option>
                  {companyTypes.map((companyType, index) => (
                    <option key={index} value={companyType}>{companyType}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showFeaturedOnly}
                    onChange={() => setShowFeaturedOnly(!showFeaturedOnly)}
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show Featured Jobs Only</span>
                </label>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6 flex justify-between items-center">
            <p className="text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, sortedJobs.length)} of {sortedJobs.length} jobs
            </p>
            <Link
              to="/jobs/post"
              className="inline-flex items-center bg-apn-blue text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Post a Job
            </Link>
          </div>

          {/* Job Listings */}
          {currentJobs.length > 0 ? (
            viewMode === 'list' ? (
              <div className="space-y-6">
                {currentJobs.map(job => (
                  <div
                    key={job.id}
                    className={`bg-white rounded-lg shadow-lg overflow-hidden border ${
                      job.featured ? 'border-apn-blue' : 'border-gray-200'
                    }`}
                  >
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold">{job.title}</h3>
                          <p className="text-gray-600">{job.company} • {job.location}</p>
                        </div>
                        <div className="mt-2 md:mt-0 flex items-start">
                          {job.featured && (
                            <span className="inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded mr-2">
                              Featured
                            </span>
                          )}
                          <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                            {job.type}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4">{job.description}</p>
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-700 mb-2">Requirements:</h4>
                        <ul className="list-disc pl-5 text-gray-600 space-y-1">
                          {job.requirements.map((req, index) => (
                            <li key={index}>{req}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                        <div className="mb-4 md:mb-0">
                          <span className="text-gray-700 font-semibold">Salary:</span> <span className="text-gray-600">{job.salary}</span>
                          <p className="text-gray-500 text-sm">Posted on {new Date(job.postedDate).toLocaleDateString()}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/jobs/${job.id}`}
                            className="inline-block bg-apn-blue text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                          >
                            View Details
                          </Link>
                          {shouldShowClosedMessage(job) ? (
                            <div className="inline-block bg-red-100 text-red-800 py-2 px-4 rounded border border-red-300">
                              No longer accepting applications
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedJobForApplication(job);
                                  setShowApplicationModal(true);
                                }}
                                className="inline-block bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition duration-200"
                              >
                                Apply Now
                              </button>
                              <button
                                onClick={() => handleReferClick(job)}
                                className="inline-block bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 transition duration-200"
                              >
                                Refer Someone
                              </button>
                            </>
                          )}
                          {isJobOwner(job) && job.status !== 'closed' && (
                            <button
                              onClick={() => handleMarkAsFilled(job.id)}
                              className="inline-block bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-700 transition duration-200"
                            >
                              Mark as Filled
                            </button>
                          )}
                          {isAdmin() && (
                            <button
                              onClick={() => handleDeleteJob(job.id, job.title)}
                              className="inline-block bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition duration-200"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentJobs.map(job => (
                  <div
                    key={job.id}
                    className={`bg-white rounded-lg shadow-lg overflow-hidden border h-full flex flex-col ${
                      job.featured ? 'border-apn-blue' : 'border-gray-200'
                    }`}
                  >
                    <div className="p-6 flex-grow">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-bold">{job.title}</h3>
                        {job.featured && (
                          <span className="inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded">
                            Featured
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">{job.company}</p>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-gray-600">
                          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>{job.type}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{job.salary}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4 line-clamp-3">{job.description}</p>
                    </div>
                    <div className="p-6 bg-gray-50 border-t border-gray-200">
                      <div className="flex flex-col space-y-3">
                        <p className="text-gray-500 text-sm">Posted {new Date(job.postedDate).toLocaleDateString()}</p>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/jobs/${job.id}`}
                            className="inline-block bg-apn-blue text-white py-1 px-3 rounded text-sm hover:bg-blue-700 transition duration-200"
                          >
                            View Details
                          </Link>
                          {shouldShowClosedMessage(job) ? (
                            <div className="inline-block bg-red-100 text-red-800 py-1 px-3 rounded text-sm border border-red-300">
                              No longer accepting applications
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedJobForApplication(job);
                                  setShowApplicationModal(true);
                                }}
                                className="inline-block bg-green-600 text-white py-1 px-3 rounded text-sm hover:bg-green-700 transition duration-200"
                              >
                                Apply Now
                              </button>
                              <button
                                onClick={() => handleReferClick(job)}
                                className="inline-block bg-purple-600 text-white py-1 px-3 rounded text-sm hover:bg-purple-700 transition duration-200"
                              >
                                Refer
                              </button>
                            </>
                          )}
                          {isJobOwner(job) && job.status !== 'closed' && (
                            <button
                              onClick={() => handleMarkAsFilled(job.id)}
                              className="inline-block bg-orange-600 text-white py-1 px-3 rounded text-sm hover:bg-orange-700 transition duration-200"
                            >
                              Filled
                            </button>
                          )}
                          {isAdmin() && (
                            <button
                              onClick={() => handleDeleteJob(job.id, job.title)}
                              className="inline-block bg-red-600 text-white py-1 px-3 rounded text-sm hover:bg-red-700 transition duration-200"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">No jobs found matching your search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex justify-center">
              <nav className="inline-flex rounded-md shadow">
                <button
                  onClick={handlePrevious}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-l-md border border-gray-300 ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 border-t border-b border-gray-300 ${
                      currentPage === page
                        ? 'bg-apn-blue text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-r-md border border-gray-300 ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </section>

      {/* Featured Employers */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Featured Employers</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['Goldman Sachs', 'JP Morgan', 'Blackstone', 'Skadden'].map((company, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 flex items-center justify-center">
                <span className="text-lg font-semibold text-gray-700">{company}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Looking to Hire?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Post your job openings on the APN Job Board to reach qualified finance professionals.
          </p>
          <Link to="/jobs/post" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Post a Job
          </Link>
        </div>
      </section>

      {/* Job Referral Modal */}
      {showReferralModal && selectedJobForReferral && (
        <JobReferralModal
          isOpen={showReferralModal}
          onClose={() => {
            setShowReferralModal(false);
            setSelectedJobForReferral(null);
          }}
          job={selectedJobForReferral}
          onSuccess={handleReferralSuccess}
        />
      )}

      {/* Application Modal */}
      {showApplicationModal && selectedJobForApplication && (
        <ApplicationModal
          isOpen={showApplicationModal}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedJobForApplication(null);
          }}
          posting={selectedJobForApplication}
          type="job"
        />
      )}
    </div>
  );
};

export default JobBoard;
